import React, { FC, useEffect, useRef, useState } from 'react';
import { DISPATCH_ACTION, PayPalButtons, usePayPalScriptReducer } from '@paypal/react-paypal-js';
import { CreateOrderActions, CreateOrderData, OnApproveActions, OnApproveData } from '@paypal/paypal-js';
import { useDispatch, useSelector } from 'react-redux';
import ComposePaypalViewModel, { IPayPalViewModel } from './viewModel';
import { PaymentActionResult } from './types';

import APIServices from '../../../../../../../../utils/services/EveryDoggyAPIServices';
import errorLogger from '../../../../../../../../utils/errorLogger/appErrorLogger';
import { toggleLoader, updatePurchaseFlow } from '../../../../../../../../reduxStore/navigation/actions';
import { RootState } from '../../../../../../../../store';
import { analyticsEventsTracker, EVENT_TRACKERS, GTMEvent, GTMOnboardingFunnelEvent, GTMQuizFunnelEvent } from '../../../../../../../../utils/analytics';
import locales from '../../../../../../../../content.json';
import aes from 'crypto-js/aes';
import enc from 'crypto-js/enc-utf8';
import styled from 'styled-components';
import lock from './img/lock.png';
import { EmptyObject } from '@reduxjs/toolkit';

function getParameterFromURLForEmail(name) {
  let url = window.location.href;
  name = name.replace(/[\[\]]/g, "\\$&");
  var regex = new RegExp("[?&]" + name + "(=([^&#]*)|&|#|$)"),
    results = regex.exec(url);
  if (!results) return null;
  if (!results[2]) return "";
  return decodeURIComponent(results[2]);
}

let emailInputValue;
let emailInputValueFromUrl = getParameterFromURLForEmail("ev");

if (emailInputValueFromUrl) {
  let emailInputValueRaw = aes.decrypt(emailInputValueFromUrl, "9213812384");
  emailInputValue = emailInputValueRaw.toString(enc);
}

const Wrapper = styled.div`
  height: 100%;
  width: 100%;
  overflow: hidden;
  border-radius: 48px;
  position: relative;
`;

const Label = styled.div`
  height: 100%;
  width: 100%;
  position: absolute;
  top: 0;
  left: 0;
  background: var(--ed-brand, #BA61FE);
  z-index: 1000;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  img {
    width: 24px;
  }

  p {
    color: #FFF;
    text-align: center;
    font-size: 22px;
    font-weight: 700;
    line-height: 28px;
    letter-spacing: 0.088px;

    ${(props) => props.lang === 'ru' && `
      font-size: 18px;
    `}

    ${(props) => props.lang === 'de' && `
      font-size: 15px;
    `}

    ${(props) => props.lang === 'fr' && `
      font-size: 19px;
    `}
  }
`;

interface Props {
  type?: string;
}

export const SubscriptionPaypalButtons: FC<Props> = ({ type }) => {
  const dispatchReduxState = useDispatch();
  const viewModel: IPayPalViewModel = ComposePaypalViewModel(window.location.href, APIServices, errorLogger);

  const paypalAmount = useSelector((state: RootState) => {
    return state.data.productList ? state.data.productList.price : '';
  });
  const paypalName = useSelector((state: RootState) => {
    return state.data.productList ? state.data.productList.product_key : '';
  });
  const paypalId = useSelector((state: RootState) => {
    return state.data.productList ? state.data.productList.id : '';
  });
  
  const [{ options }, dispatch] = usePayPalScriptReducer();
  const [{ isResolved }] = usePayPalScriptReducer();
  const [email, setEmail] = useState(null);
  const [payPalSDK, setPayPalSDK] = useState(false);

  const paypalAmountRef = useRef(paypalAmount);
  const paypalNameRef = useRef(paypalName);
  const paypalIdRef = useRef(paypalId);

  const lang = document.documentElement.lang;

  useEffect(() => {
    paypalAmountRef.current = paypalAmount;
    paypalNameRef.current = paypalName;
    paypalIdRef.current = paypalId;
  }, [paypalAmount, paypalName, paypalId]);

  const togglePurchaseFlow = (upselPDF: boolean) => {
    dispatchReduxState(updatePurchaseFlow({upselPDF: upselPDF}));
  };
  useEffect(() => {
    if(emailInputValue) {
      setEmail(emailInputValue)
    }
  }, [])

  useEffect(() => {
    if (isResolved) {
      setPayPalSDK(true);
    }
  }, [isResolved]);

  useEffect(() => {
    dispatch({
      type: DISPATCH_ACTION.RESET_OPTIONS,
      value: {
        ...options,
        intent: 'capture',
      },
    });
  }, [type]);

  const sendCreateSubscriptionEvents = async (
    productName: string,
    productPrice: number
  ) => {

    const event = GTMQuizFunnelEvent.tryPaid({
      paymentType: 'paypal',
      trialPrice: `${productPrice}`,
      productName: `${productName}`,
    });
    analyticsEventsTracker.trackWithTarget(event, EVENT_TRACKERS.GTM);

    return Promise.resolve();
  };

  const createPaypalOrder = async (data: CreateOrderData, actions: CreateOrderActions): Promise<string> => {
    const currentPaypalName = paypalNameRef.current || '';
    let productType: string;
    if (currentPaypalName === 'puppy_guide') {
      productType = 'puppy';
    } else {
      productType = 'adult';
    }
    const currentPayPalId = paypalIdRef.current || null;
    analyticsEventsTracker.trackWithTarget(GTMOnboardingFunnelEvent.paymentIntentionPdf(currentPaypalName, productType,'paypal', paypalAmountRef.current, currentPayPalId), EVENT_TRACKERS.GTM);
  
    return viewModel
      .createOrderPaypal(currentPayPalId)
      .then((result) => {
        if(result && result.order_id) {
          return result.order_id;
        } else {
          throw new Error('order_id is missing in the response.');
        }
  
      })
      .catch((error) => {
        const paypalWarnings = document.querySelector('.paypalWarnings') as HTMLElement;
        paypalWarnings.classList.remove('disableScroll');
        paypalWarnings.style.display = 'block';
        paypalWarnings.innerHTML = `Error: ${error.message || ''}. Please connect <a target="_blank" href="mailto:support@verv.com?subject=Problem with login (simple-login)">support</a>`;
        
        throw new Error('Failed to create PayPal order');
      });
  };

  const handleApprove = (data: OnApproveData, actions: OnApproveActions) => {
    dispatchReduxState(toggleLoader(true));
    const currentPayPalId = paypalIdRef.current || null;
    return viewModel
      .processPaymentAction(data, actions)
      .then((result: EmptyObject) => {
        setTimeout(() => {
          if(result) {
            window.paypalPayment = true;
            const currentPaypalName = paypalNameRef.current || '';
            let productType: string;
            if (currentPaypalName === 'puppy_guide') {
              productType = 'puppy';
            } else {
              productType = 'adult';
            }
            analyticsEventsTracker.trackWithTarget(GTMOnboardingFunnelEvent.paymentSuccessfulPdf(currentPaypalName, productType,'paypal', paypalAmountRef.current, currentPayPalId), EVENT_TRACKERS.GTM);
            dispatchReduxState(toggleLoader(false));
            togglePurchaseFlow(true);
          }
        }, 1000);

        const gtmPurchaseEvent = GTMEvent.purchase({
          value: paypalAmountRef.current,
        });
        analyticsEventsTracker.trackWithTarget(gtmPurchaseEvent, EVENT_TRACKERS.GTM);
      })
      .catch(error => {
        dispatchReduxState(toggleLoader(false));
        const paypalWarnings = document.querySelector('.paypalWarnings') as HTMLElement;
        paypalWarnings.classList.remove('disableScroll');
        paypalWarnings.style.display = 'block';
        if (error.message && error.message.includes('Digital product purchase')) {
          paypalWarnings.innerHTML = 'You have already purchased this product.';
        } else {
          paypalWarnings.innerHTML = `${error.message || 'Something went wrong'}`;
        }
      });
  };

  return (
    payPalSDK && (
      <Wrapper>
        <Label lang={lang}>
          <img src={lock} alt="lock" />
          <p>
            {locales[window.userLang].pdfUpselling_14}
          </p>
        </Label>
        <PayPalButtons
          style={{ height: 55 }}
          createOrder={(data, actions) => createPaypalOrder(data, actions)}
          onApprove={handleApprove}
          onCancel={() => {
            const htmlNode = document.querySelector('html') as HTMLElement;
            htmlNode?.classList.remove('disableScroll');

            analyticsEventsTracker.trackWithTarget(
              GTMOnboardingFunnelEvent.paymentAttemptClosed({
                payment_system: 'paypal',
                product_name: paypalName,
              }),
            EVENT_TRACKERS.GTM);

            return viewModel.processPaymentCancel();
          }}
          onError={err => {
            const htmlNode = document.querySelector('html') as HTMLElement;
            htmlNode?.classList.remove('disableScroll');
            // document.querySelector(".paypalWarnings").innerHTML = "error";

            const error: Error = {
              name: 'PayPal payment error',
              message: JSON.stringify(err),
            };
            return viewModel.processPaymentError(error);
          }}
        />
      </Wrapper>
    )
  );
};
